/* eslint-disable mui-unused-classes/unused-classes */
import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import Tooltip from '@mui/material/Tooltip'

import { SquareIconButton } from '@/modules/base/components/buttons/index'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import IconMove from 'assets/images/icons/send_file.svg'

import lang from '@/lang'
import customDecodeUri from '@/utils/customDecodeUri'
import TextEllipsis from '@/modules/base/components/texts/text-ellipsis'
import { previewMimeTypes } from '@/modules/document/components/document-drawer/document-drawer'
import { getMimeTypeByExtension } from '../_helpers/getMimeTypeByExtension'
import { humanFileSize } from '@/modules/_helpers/humanFileSize'
import getFileIcon from '@/modules/file/_helpers/getFileIcon'
const MOVE_DELAY = 3000

const DownloadMaterialIcon = ({ fill, height, style, width, ...props }) => (
  <DownloadRoundedIcon {...props} htmlColor={fill} style={{ ...style, height, width }} />
)

const VisibilityMaterialIcon = ({ fill, height, style, width, ...props }) => (
  <VisibilityRoundedIcon {...props} htmlColor={fill} style={{ ...style, height, width }} />
)

const CloseMaterialIcon = ({ fill, height, style, width, ...props }) => (
  <CloseRoundedIcon {...props} htmlColor={fill} style={{ ...style, height, width }} />
)

const formatFileSize = (size) => {
  const numericSize = Number(size)
  if (!Number.isFinite(numericSize) || numericSize < 0) {
    return null
  }
  return humanFileSize(numericSize, false, 1).replace('.', ',')
}

const useStyles = makeStyles(theme => ({
  indicator: {
    position: 'absolute',
    overflow: 'hidden',
    width: 30,
    height: 30,
    left: -46,
    bottom: 9,
    '& > div': {
      border: theme.border,
      transform: 'translate(-7px, 0px) rotate(45deg)',
      width: '100%',
      height: '100%',
      boxShadow: '0 3px 5px 0 rgba(206, 216, 240, 0.8)',
    },
  },
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${(theme.borderColors && theme.borderColors.default) || theme.borderColor || '#dee2ed'}`,
    borderRadius: 6,
    minHeight: 74,
    padding: '10px 14px',
    backgroundColor: theme.white,
    transition: 'border-color 120ms ease',
    '&:hover $itemButtons': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    '&:hover .tdButtonSection': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    '& + $root': {
      marginTop: 10,
    },
  },
  rootPreviewActive: {
    borderColor: theme.primary,
  },
  fileIcon: {
    width: 24,
    minWidth: 24,
    marginRight: 12,
    display: 'flex',
    justifyContent: 'center',
  },
  fileInfo: {
    flexGrow: 1,
    minWidth: 0,
  },
  fileName: {
    maxWidth: 350,
  },
  item: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: theme.fontSizes.default,
    color: theme.black,
  },
  fileSize: {
    fontSize: theme.fontSizes.smaller,
    color: theme.textMuted,
    lineHeight: '18px',
  },
  itemButtons: {
    display: 'flex',
    alignItems: 'center',
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 120ms ease',
    '& > *': {
      paddingRight: 5,
      paddingLeft: 5,
    },
  },
  itemButtonsVisible: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  accessDeniedIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    cursor: 'help',
  },
  accessDeniedIcon: {
    '& path': {
      fill: theme.error,
    },
  },
}))

const TodoFile = (props) => {
  const classes = useStyles()
  const { file, className, onPreview, onDownLoad, onUnlink, readOnly, onMove, currentPreview } = props

  const fileMimeType = file?.mimetype ?? getMimeTypeByExtension(file?.name)
  const hasPreview = previewMimeTypes.includes(fileMimeType)
  const isAccessDenied = file?.accessDenied === true

  if (!file) {
    return null
  }

  const { name, id, size } = file
  const icon = getFileIcon(fileMimeType, name)
  const decodedName = customDecodeUri(name)
  const formattedSize = formatFileSize(size)
  const isPreviewActive = currentPreview?.id === id

  return (
    <div className={clsx(classes.root, className, isPreviewActive && classes.rootPreviewActive)}>
      {isPreviewActive && <div className={classes.indicator}><div /></div>}
      <div className={classes.fileIcon}>
        <SVGIcon svg={icon} width='18px' height='22px' />
      </div>
      <div className={classes.fileInfo}>
        <TextEllipsis title={decodedName} className={classes.fileName}>
          <div className={classes.item}>{decodedName}</div>
        </TextEllipsis>
        {formattedSize && <div className={classes.fileSize}>{formattedSize}</div>}
      </div>
      <div className={clsx(classes.itemButtons, 'tdButtonSection', isAccessDenied && classes.itemButtonsVisible)}>
        {isAccessDenied
          ? (
            <Tooltip title={lang.todo.detail.accessDeniedFileTooltip}>
              <div className={classes.accessDeniedIconWrapper}>
                <ErrorOutlineRoundedIcon className={classes.accessDeniedIcon} style={{ width: 22, height: 22 }} />
              </div>
            </Tooltip>
          )
          : (
            <>
              {Boolean(onMove) && (
                <SquareIconButton
                  tooltip={lang.todo.iconButtons.move}
                  size='medium'
                  icon={IconMove}
                  onClick={() => onMove && onMove(id, name)}
                  debounceTime={MOVE_DELAY}
                />
              )}
              <SquareIconButton
                tooltip={lang.todo.iconButtons.download}
                size='medium'
                icon={DownloadMaterialIcon}
                disabled={!onDownLoad}
                onClick={() => onDownLoad && onDownLoad(id, name)}
              />
              {hasPreview && (
                <SquareIconButton
                  tooltip={lang.todo.iconButtons.preview}
                  size='medium'
                  icon={VisibilityMaterialIcon}
                  disabled={!onPreview}
                  onClick={() => onPreview && onPreview(id, name)}
                />
              )}
              {!readOnly
                ? (
                  <SquareIconButton
                    tooltip={lang.todo.iconButtons.unlink}
                    size='medium'
                    icon={CloseMaterialIcon}
                    disabled={!onUnlink || readOnly}
                    onClick={() => onUnlink && onUnlink(id, name)}
                  />
                )
                : null}
            </>
          )}
      </div>
    </div>
  )
}

const materialIconPropTypes = {
  fill: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

DownloadMaterialIcon.propTypes = materialIconPropTypes
VisibilityMaterialIcon.propTypes = materialIconPropTypes
CloseMaterialIcon.propTypes = materialIconPropTypes

TodoFile.propTypes = {
  file: PropTypes.object.isRequired,
  className: PropTypes.string,
  onPreview: PropTypes.func,
  onDownLoad: PropTypes.func,
  onUnlink: PropTypes.func,
  readOnly: PropTypes.bool,
  onMove: PropTypes.func,
  currentPreview: PropTypes.object,
}

export default TodoFile
